/**
 * The legacy client's `adhesion-settings` object: one owner for the key, one
 * safe read, one merge-write.
 *
 * Four modules used to hand-roll the same try/catch JSON round-trip against this
 * key — SettingsModal, LangProvider, i18n and now the board — and every one of
 * them had to remember that a write is read-modify-write, because `language` and
 * the rest live in the same object and a plain `setItem` drops whichever half the
 * writer did not know about.
 *
 * Every access is wrapped: localStorage is not merely empty in private mode or
 * with site data blocked, it throws on access.
 */

export const SETTINGS_KEY = 'adhesion-settings';

export interface GameSettings {
  soundEnabled: boolean;
  musicEnabled: boolean;
  soundVolume: number;
  musicVolume: number;
  animationSpeed: 'slow' | 'normal' | 'fast';
  showCoordinates: boolean;
  highlightMoves: boolean;
  confirmMoves: boolean;
}

export const SETTINGS_DEFAULTS: GameSettings = {
  soundEnabled: true,
  musicEnabled: true,
  soundVolume: 70,
  musicVolume: 50,
  animationSpeed: 'normal',
  showCoordinates: false,
  highlightMoves: true,
  confirmMoves: false,
};

/** The stored object, or `{}` when there is nothing readable there. */
export function readSettings(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Merge `patch` into the stored object, leaving keys it does not name alone. */
export function persistSettings(patch: object): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify({ ...readSettings(), ...patch }));
  } catch {
    /* ignore write failures (private mode etc.) */
  }
}

/**
 * Whether the board should label its rows and columns.
 *
 * Read at mount rather than subscribed to: both places that set it — legacy's
 * `SettingsModal`, reached from `MainMenu`, and the redesign's `/settings`
 * route — are screens the board is not on, so the toggle cannot move while a
 * board is mounted.
 */
export function getShowCoordinates(): boolean {
  return readSettings().showCoordinates === true;
}

/**
 * Set it. `GameBoard` has implemented coordinates in full the whole time and
 * read this key for them, while the redesign shipped no control that wrote it —
 * so a player who never opened the legacy menu could not turn them on.
 */
export function setShowCoordinates(on: boolean): void {
  persistSettings({ showCoordinates: on });
}
